import React, { useRef, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail, friendlyError } from "../lib/api";
import { ShieldCheck, Camera, CheckCircle2, Clock, X } from "lucide-react";
import { toast } from "sonner";

export default function VerifyPage() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (loading) return <div className="min-h-screen bg-paper grid place-items-center text-slate-500">Načítám…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const onChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(f.type)) {
      setError("Povolené formáty: JPG, PNG, WebP.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError("Maximum je 10 MB.");
      return;
    }
    setError("");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/id-card", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      toast.success("Fotka občanky odeslána. Brzy se vrátíme s ověřením.");
      navigate("/profile");
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const status = user.verification_status;

  return (
    <div className="min-h-screen bg-paper py-10 px-4">
      <div className="max-w-xl mx-auto">
        <Link to="/" className="font-heading font-extrabold text-2xl tracking-tight text-slate-900 flex items-center gap-0.5 mb-8" data-testid="verify-back-logo">
          ConnectYou<span className="text-sky-500">.</span>
        </Link>

        <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-600 grid place-items-center">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Ověření občanky</h1>
              <p className="text-slate-500 font-medium">Krok 2 z 2 — bezpečnost komunity.</p>
            </div>
          </div>

          {/* Safety notice */}
          <div className="mt-6 rounded-2xl bg-sky-50 border border-sky-100 p-4 text-sm text-sky-900" data-testid="verify-safety-notice">
            <p className="font-bold">Občanka je požadovaná kvůli bezpečnosti komunity.</p>
            <p className="mt-1.5">Máme to proto, abychom v případě porušení pravidel, obtěžování nebo problému při cestě (zejména do jiných zemí) měli jasný záznam, kdo se akce zúčastnil. Fotka občanky se nikde nezobrazuje, je šifrovaná a má k ní přístup výhradně admin tým ConnectYou. Veškerá manipulace je trestná podle českého zákona.</p>
            <p className="mt-2 text-xs font-bold uppercase tracking-[0.12em]">Ověřujeme: jméno a věk se shoduje s registrací.</p>
          </div>

          {/* Status display */}
          {status === "submitted" && (
            <div className="mt-5 rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-center gap-3" data-testid="verify-status-submitted">
              <Clock className="w-5 h-5 text-amber-600" />
              <div className="text-sm">
                <p className="font-bold text-amber-800">Občanka odeslána, čeká na schválení.</p>
                <p className="text-amber-700/90">Obvykle do 24 hodin. Můžeš zatím procházet akce.</p>
              </div>
            </div>
          )}
          {status === "approved" && (
            <div className="mt-5 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3" data-testid="verify-status-approved">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <p className="text-sm font-bold text-emerald-800">Profil ověřen. Můžeš se připojit k jakékoli akci.</p>
            </div>
          )}
          {status === "rejected" && (
            <div className="mt-5 rounded-2xl bg-red-50 border border-red-200 p-4 flex items-center gap-3" data-testid="verify-status-rejected">
              <X className="w-5 h-5 text-red-600" />
              <div className="text-sm">
                <p className="font-bold text-red-800">Ověření zamítnuto.</p>
                <p className="text-red-700/90">Zkus nahrát ostřejší fotku, kde je jasně vidět jméno a datum narození.</p>
              </div>
            </div>
          )}

          {/* Upload */}
          {status !== "approved" && (
            <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
              <div
                onClick={() => fileRef.current?.click()}
                data-testid="verify-dropzone"
                className="relative cursor-pointer border-2 border-dashed border-slate-300 hover:border-sky-400 rounded-2xl p-8 text-center transition-colors bg-slate-50/50"
              >
                {preview ? (
                  <img src={preview} alt="náhled" className="max-h-72 mx-auto rounded-xl" data-testid="verify-preview" />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-slate-500">
                    <Camera className="w-10 h-10 text-slate-400" />
                    <p className="font-bold text-slate-700">Klikni a vyfoť / nahraj občanku</p>
                    <p className="text-xs">JPG, PNG nebo WebP · max 10 MB</p>
                  </div>
                )}
                <input
                  ref={fileRef}
                  data-testid="verify-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={onChange}
                />
              </div>

              {error && (
                <div data-testid="verify-error" className="rounded-xl bg-red-50 text-red-700 text-sm font-semibold p-3">{error}</div>
              )}

              <button
                data-testid="verify-submit-btn"
                type="submit" disabled={!file || submitting}
                className="px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-white font-bold transition-all active:scale-95 shadow-[0_12px_30px_-10px_rgba(14,165,233,0.55)]"
              >
                {submitting ? "Nahrávám…" : status === "rejected" ? "Zkusit znovu" : "Odeslat k ověření"}
              </button>

              <Link
                data-testid="verify-skip-link"
                to="/"
                className="text-center text-sm text-slate-500 hover:text-slate-700 font-bold mt-1"
              >
                Procházet akce zatím bez ověření →
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
